import { useState, useEffect, useRef } from "react";
import { AlertTriangle, Trash2, Shield, RefreshCw, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useEmpresa } from "../contexts/EmpresaContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 3000;

export default function ResetSistema() {
  const { empresaActual } = useEmpresa();
  const empresaId = empresaActual?.id || localStorage.getItem("empresaActualId");
  const [modulos, setModulos] = useState({});
  const [seleccionados, setSeleccionados] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [step, setStep] = useState(1); // 1: elegir, 2: confirmar texto, 3: progreso/hecho
  const [textoConfirmacion, setTextoConfirmacion] = useState("");
  const [jobEstado, setJobEstado] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reset-sistema/modulos`)
      .then((res) => setModulos(res.data))
      .catch(() => toast.error("No se pudo cargar la lista de módulos"));
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const toggleModulo = (key) => {
    setSeleccionados((prev) => prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!empresaId) {
      toast.error("Selecciona una empresa primero");
      return;
    }
    if (seleccionados.length === 0) {
      toast.error("Elige al menos un módulo");
      return;
    }
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await axios.get(`${API}/reset-sistema/preview`, {
        params: { modulos: seleccionados.join(","), empresa_id: empresaId },
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al previsualizar");
    } finally {
      setLoadingPreview(false);
    }
  };

  const pollEstado = () => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/reset-sistema/estado`);
        setJobEstado(res.data);
        if (res.data.estado === "listo" || res.data.estado === "error") {
          clearInterval(pollRef.current);
          if (res.data.estado === "listo") toast.success("Reset completado");
          else toast.error(res.data.error || "Error durante el reset");
        }
      } catch (err) {
        // hiccup de red, se reintenta en el próximo tick
      }
    }, POLL_MS);
  };

  const handleEjecutar = async () => {
    if (textoConfirmacion !== "RESETEAR") {
      toast.error('Escribe exactamente "RESETEAR" para confirmar');
      return;
    }
    try {
      const res = await axios.post(`${API}/reset-sistema/ejecutar`, null, {
        params: { modulos: seleccionados.join(","), empresa_id: empresaId, confirmar: true, aplicar: true },
      });
      setJobEstado({ estado: "corriendo", modulos_completados: [], modulos: res.data.modulos });
      setStep(3);
      pollEstado();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar el reset");
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 p-6" data-testid="reset-sistema-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-900/40">
            <Trash2 size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Reset del Sistema a Cero</h1>
          <p className="text-gray-400 text-sm">Elige los módulos, mira el total combinado y confirmá. Solo superadmin.</p>
        </div>

        {step === 1 && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-3 bg-amber-900/20 border border-amber-800 rounded-xl p-4 mb-6">
              <Shield size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-amber-200 text-xs">
                Nunca toca usuarios, empresas, galpones, productos, RRHH, asistencia ni auditoría.
                Se genera un backup JSON por módulo en el servidor antes de borrar cualquier dato.
              </p>
            </div>

            <label className="text-sm text-gray-400 mb-2 block">Tildá los módulos a resetear (podés elegir varios juntos)</label>
            <div className="grid gap-2 mb-5" data-testid="lista-modulos-reset">
              {Object.entries(modulos).map(([key, info]) => (
                <label
                  key={key}
                  className={`flex items-start gap-3 text-left px-4 py-3 rounded-xl border transition-colors cursor-pointer ${
                    seleccionados.includes(key) ? "border-red-600 bg-red-950/30" : "border-gray-800 bg-gray-800/50 hover:bg-gray-800"
                  }`}
                  data-testid={`checkbox-modulo-${key}`}
                >
                  <input
                    type="checkbox"
                    checked={seleccionados.includes(key)}
                    onChange={() => toggleModulo(key)}
                    className="mt-1 accent-red-600"
                  />
                  <div>
                    <p className="text-white text-sm font-medium">{info.label}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{info.colecciones.join(", ")}</p>
                    {info.advertencia_huerfanos && (
                      <p className="text-amber-400 text-xs mt-1">⚠ {info.advertencia_huerfanos}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>

            <button
              onClick={handlePreview}
              disabled={loadingPreview || seleccionados.length === 0}
              data-testid="btn-ver-preview-reset"
              className="w-full py-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
            >
              {loadingPreview ? "Calculando..." : "Ver vista previa combinada"}
            </button>
          </div>
        )}

        {step === 1 && preview && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6" data-testid="reset-preview-card">
            <h3 className="text-white font-semibold mb-3">Vista previa combinada</h3>
            <div className="space-y-3 mb-4">
              {preview.modulos.map((m) => (
                <div key={m.modulo} className="border-b border-gray-800 pb-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300 font-medium">{m.label}</span>
                    <span className="text-white font-mono">{m.total_registros_a_eliminar}</span>
                  </div>
                </div>
              ))}
            </div>
            {preview.advertencias_huerfanos?.length > 0 && (
              <div className="bg-amber-900/20 border border-amber-800 rounded-lg p-3 mb-4">
                {preview.advertencias_huerfanos.map((a, i) => (
                  <p key={i} className="text-amber-200 text-xs mb-1">⚠ {a}</p>
                ))}
              </div>
            )}
            <div className="border-t border-gray-800 pt-3 flex justify-between items-center">
              <span className="text-gray-300 text-sm font-semibold">Total a eliminar (todos los módulos tildados)</span>
              <span data-testid="reset-total-registros" className="text-red-400 font-bold text-lg">{preview.total_registros_a_eliminar}</span>
            </div>
            {preview.total_registros_a_eliminar > 0 ? (
              <button
                onClick={() => setStep(2)}
                data-testid="btn-continuar-reset"
                className="w-full mt-5 py-3 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <AlertTriangle size={18} /> Continuar con el reset
              </button>
            ) : (
              <p className="text-gray-500 text-sm mt-4 text-center">No hay registros para eliminar en los módulos elegidos.</p>
            )}
          </div>
        )}

        {step === 2 && preview && (
          <div className="bg-gray-900 border-2 border-red-700 rounded-2xl p-6" data-testid="reset-confirmacion-final">
            <div className="flex items-start gap-3 bg-red-950/40 border border-red-800 rounded-xl p-4 mb-5">
              <AlertTriangle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-200 text-sm">
                Vas a eliminar <strong>{preview.total_registros_a_eliminar}</strong> registros de{" "}
                <strong>{preview.modulos.length}</strong> módulo(s) de forma definitiva (queda un backup JSON
                por módulo en el servidor, pero no hay botón de "deshacer" en la UI).
              </p>
            </div>
            <label className="text-sm text-gray-400 mb-2 block">
              Escribe <strong className="text-white">RESETEAR</strong> para confirmar
            </label>
            <input
              value={textoConfirmacion}
              onChange={(e) => setTextoConfirmacion(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white mb-4"
              data-testid="input-confirmacion-reset"
              placeholder="RESETEAR"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl font-semibold"
                data-testid="btn-cancelar-reset"
              >
                Cancelar
              </button>
              <button
                onClick={handleEjecutar}
                disabled={textoConfirmacion !== "RESETEAR"}
                className="flex-1 py-3 bg-red-600 hover:bg-red-500 disabled:bg-red-900 disabled:opacity-50 text-white rounded-xl font-semibold"
                data-testid="btn-confirmar-reset"
              >
                Eliminar definitivamente
              </button>
            </div>
          </div>
        )}

        {step === 3 && jobEstado && (
          <div className={`bg-gray-900 border rounded-2xl p-6 ${jobEstado.estado === "listo" ? "border-emerald-700" : jobEstado.estado === "error" ? "border-red-700" : "border-blue-700"}`} data-testid="reset-progreso-card">
            <div className="flex items-center gap-3 mb-4">
              {jobEstado.estado === "listo" ? (
                <CheckCircle size={24} className="text-emerald-400" />
              ) : jobEstado.estado === "error" ? (
                <AlertTriangle size={24} className="text-red-400" />
              ) : (
                <Loader2 size={24} className="text-blue-400 animate-spin" />
              )}
              <h3 className="text-white font-semibold">
                {jobEstado.estado === "listo" ? "Reset completado" : jobEstado.estado === "error" ? "Error en el reset" : "Reseteando..."}
              </h3>
            </div>
            <p className="text-gray-400 text-sm mb-3" data-testid="reset-progreso-texto">{jobEstado.progreso}</p>
            <div className="space-y-1 mb-3">
              {(jobEstado.modulos || seleccionados).map((m) => (
                <div key={m} className="flex justify-between text-sm">
                  <span className="text-gray-400">{modulos[m]?.label || m}</span>
                  <span className={jobEstado.modulos_completados?.includes(m) ? "text-emerald-400" : "text-gray-600"}>
                    {jobEstado.modulos_completados?.includes(m) ? `✓ ${jobEstado.eliminados_por_modulo?.[m] ?? ""}` : "pendiente"}
                  </span>
                </div>
              ))}
            </div>
            {jobEstado.error && <p className="text-red-400 text-xs">{jobEstado.error}</p>}
            {jobEstado.estado === "listo" && (
              <p className="text-gray-500 text-xs mt-3">Total eliminado: {jobEstado.total_eliminados}. Backups guardados en el servidor.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
